import cx from 'classnames'

import Styles from './PlanDetails.module.scss'

import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import ProIcon from 'blaze-ui/icons/20/ProIcon'
import TeamPlanIcon from 'blaze-ui/icons/20/TeamPlanIcon'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { Button } from 'blaze-ui/Button'
import { ButtonVariant } from 'blaze-ui/Button/Types'
import { FeatureList } from './FeatureList'
import { AgencyPlanIcon, Cloud } from 'blaze-ui/icons'
import { ButtonLink } from 'blaze-ui/Button/ButtonLink'
import { useRootStore } from 'stores/RootStore'
import dayjs from 'dayjs'
import { formatPrice } from 'components/Settings/TieredBilling/utils'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'

export const planOrder: any = {
  [PaymentPlanKey.Free]: 0,
  [PaymentPlanKey.BlazeAutopilotStarter]: 1,
  [PaymentPlanKey.VirtualMarketer]: 1,
  [PaymentPlanKey.BlazeAutopilotGrowth]: 2,
  [PaymentPlanKey.BlazeAutopilotEnterprise]: 3,
}

export const iconMap: any = {
  [PaymentPlanKey.Free]: Cloud,
  [PaymentPlanKey.BlazeAutopilotStarter]: TeamPlanIcon,
  [PaymentPlanKey.BlazeAutopilotGrowth]: ProIcon,
  [PaymentPlanKey.BlazeAutopilotEnterprise]: AgencyPlanIcon,
}

export const nameMap: any = {
  [PaymentPlanKey.Free]: 'Free',
  [PaymentPlanKey.BlazeAutopilotStarter]: 'Starter',
  [PaymentPlanKey.VirtualMarketer]: 'Pioneer',
  [PaymentPlanKey.BlazeAutopilotGrowth]: 'Growth',
  [PaymentPlanKey.BlazeAutopilotEnterprise]: 'Enterprise',
}

export interface CurrentPlan {
  monthlyPrice: number | string
  length?: number
  key: PaymentPlanKey
}

export const PlanDetails = ({
  planKey,
  monthlyPrice,
  currentPlan,
  onSelectPlan,
  isTrialing,
  isProcessing,
  buttonTextOverride,
}: {
  planKey: PaymentPlanKey
  monthlyPrice?: number
  currentPlan: CurrentPlan
  onSelectPlan: () => void
  isTrialing: boolean
  isProcessing: boolean
  buttonTextOverride?: string
}) => {
  const isSystemRedesignEnabled = useIsSystemRedesign()
  const { currentSubscription } = useRootStore().tierUsageStore

  if (!monthlyPrice && monthlyPrice !== 0) return null

  const price = currentPlan.key === planKey ? currentPlan.monthlyPrice : formatPrice(monthlyPrice)
  const currentPlanKey = currentPlan.key
  const Icon = iconMap[planKey]
  const name = nameMap[planKey]
  let buttonVariant: ButtonVariant = planOrder[currentPlanKey] >= planOrder[planKey] ? 'tertiary' : 'ghost-tertiary'
  if (planOrder[currentPlanKey] === planOrder[planKey] && currentPlanKey === PaymentPlanKey.VirtualMarketer) {
    buttonVariant = 'tertiary'
  }
  if (isTrialing && planKey !== PaymentPlanKey.Free) {
    buttonVariant = 'ghost-tertiary'
  }
  if (isSystemRedesignEnabled) {
    buttonVariant = 'secondary'
  }
  if (currentSubscription?.cancel_at && currentPlanKey === planKey) {
    buttonVariant = 'primary'
  }
  const buttonText = () => {
    if (buttonTextOverride) {
      return buttonTextOverride
    }

    if (
      currentSubscription?.status === 'canceled' ||
      (planKey === PaymentPlanKey.Free && currentPlanKey !== PaymentPlanKey.Free)
    ) {
      return 'Select Plan'
    }

    if (currentPlanKey === PaymentPlanKey.VirtualMarketer && planKey === PaymentPlanKey.BlazeAutopilotStarter) {
      return 'Downgrade'
    }

    if (currentPlanKey === PaymentPlanKey.VirtualMarketer) {
      return 'Upgrade'
    }

    if (isTrialing && currentPlanKey === planKey) {
      return 'Purchase'
    }

    if (isTrialing && currentPlanKey !== planKey) {
      return 'Try Free'
    }

    const currentPlanIndex = planOrder[currentPlanKey]
    const newPlanIndex = planOrder[planKey]

    if (currentPlanIndex === newPlanIndex) {
      if (currentSubscription?.cancel_at || isTrialing) {
        return 'Reactivate'
      }

      if (currentPlanKey === PaymentPlanKey.Free) {
        return 'Current Plan'
      }

      return 'Change Plan Length'
    }

    if (newPlanIndex > currentPlanIndex) {
      if (isSystemRedesignEnabled) {
        if (planKey === PaymentPlanKey.BlazeAutopilotGrowth) return 'Change Plan Length'
        if (planKey === PaymentPlanKey.BlazeAutopilotStarter) return 'Select Plan'
      }

      return 'Upgrade'
    }

    return 'Downgrade'
  }

  const currentPlanPillText = () => {
    if (currentSubscription?.cancel_at) {
      const expirationDate = dayjs(currentSubscription?.cancel_at).format('M/D')

      if (isTrialing) {
        return `Expiring ${expirationDate}`
      }

      if (currentSubscription?.status === 'active') {
        return `Cancels ${dayjs(currentSubscription?.cancel_at).format('M/D')}`
      }
    }

    if (isTrialing) {
      return 'On Free Trial'
    }

    return 'Current Plan'
  }

  const buttonIsDisabled = isProcessing || (currentPlanKey === PaymentPlanKey.Free && planKey === PaymentPlanKey.Free)
  const isFreeAndIsOnFree = planKey === PaymentPlanKey.Free && currentPlanKey === PaymentPlanKey.Free
  const showButton = planKey !== PaymentPlanKey.BlazeAutopilotEnterprise && !isFreeAndIsOnFree

  return (
    <div className={cx(Styles.planDetails, Styles[planKey])}>
      {currentPlanKey === planKey && isSystemRedesignEnabled && (
        <div className={cx(Styles.currentPlanPill, Styles[planKey])}>{currentPlanPillText()}</div>
      )}
      <div className={cx(Styles.header, Styles[planKey])}>
        <div className={Styles.left}>
          {!isSystemRedesignEnabled && <Icon color="var(--light-100)" />}
          <Heading level={4} color="var(--light-100)">
            {name}
          </Heading>
          {isSystemRedesignEnabled && (
            <Heading level={4} color="var(--light-100)">
              {planKey !== PaymentPlanKey.BlazeAutopilotEnterprise && `$${price}/mo`}
            </Heading>
          )}
        </div>
        {currentPlanKey === planKey && !isSystemRedesignEnabled && (
          <div className={Styles.currentPlanPill}>{currentPlanPillText()}</div>
        )}
      </div>
      {(!isSystemRedesignEnabled || planKey !== PaymentPlanKey.Free) && (
        <div className={Styles.priceAndCta}>
          {!isSystemRedesignEnabled && (
            <div className={Styles.pricePerMonth}>
              {planKey === PaymentPlanKey.BlazeAutopilotEnterprise ? (
                <Heading level={2}>Let's Talk</Heading>
              ) : (
                <>
                  <Heading level={2}>${price}</Heading>
                  <Text variant="placeholder" color="var(--dark-80)">
                    /mo
                  </Text>
                </>
              )}
            </div>
          )}
          {planKey === PaymentPlanKey.BlazeAutopilotEnterprise && (
            <ButtonLink
              elementType="a"
              target="_blank"
              variant={isSystemRedesignEnabled ? 'secondary' : 'ghost-tertiary'}
              fullWidth
              to="https://form.typeform.com/to/l7gzfLGE"
            >
              Book a Demo
            </ButtonLink>
          )}
          {showButton && (
            <Button variant={buttonVariant} fullWidth onPress={onSelectPlan} isDisabled={buttonIsDisabled}>
              {buttonText()}
            </Button>
          )}
          <div className={Styles.description}>
            <Text
              variant={isSystemRedesignEnabled ? 'metadata' : 'label'}
              color={isSystemRedesignEnabled ? 'var(--dark-90)' : 'var(--dark-60)'}
            >
              {planKey === PaymentPlanKey.Free && <span>For early businesses and getting to know Blaze</span>}
              {planKey === PaymentPlanKey.BlazeAutopilotStarter && (
                <span>
                  Go unlimited with {isSystemRedesignEnabled ? 'no watermarks, ' : <strong>no watermarks,</strong>} just
                  content that looks 100% yours.
                </span>
              )}
              {planKey === PaymentPlanKey.BlazeAutopilotGrowth && (
                <span>For sophisticated planning, team collaboration, and paid ads.</span>
              )}
              {planKey === PaymentPlanKey.BlazeAutopilotEnterprise && (
                <span>For agencies and marketing teams managing multiple brands</span>
              )}
            </Text>
          </div>
        </div>
      )}
      <FeatureList plan={planKey} currentPlan={currentPlan.key} />
    </div>
  )
}
